import React, { useState, useEffect, useCallback } from 'react';

const PROVIDERS = [
  { value: 'CLAUDE', label: 'Claude (Anthropic)' },
  { value: 'OPENAI', label: 'OpenAI' },
];

const MODELS = {
  CLAUDE: [
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Fast, Low Cost)' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Balanced)' },
  ],
  OPENAI: [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast, Low Cost)' },
    { value: 'gpt-4o', label: 'GPT-4o (Balanced)' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini (Latest, Fast)' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano (Cheapest)' },
  ],
};

const AI_FEATURES = [
  {
    key: 'REVENUE_MANAGER',
    label: 'Revenue Manager — Dynamic Pricing',
    description: 'AI analyzes historical occupancy, booking pace, and market conditions to recommend optimal room rates daily.',
    icon: '\u2191',
    color: 'var(--theme-success)',
  },
  {
    key: 'GUEST_SENTIMENT',
    label: 'Guest Sentiment Intelligence',
    description: 'AI analyzes guest feedback comments to extract sentiment, categorize issues, detect problem rooms, and flag urgent concerns.',
    icon: '\u2661',
    color: '#7c3aed',
  },
  {
    key: 'NO_SHOW_PREDICTOR',
    label: 'No-Show & Cancellation Predictor',
    description: 'AI scores upcoming reservations for no-show probability based on guest history, payment status, booking source, and other risk factors.',
    icon: '!',
    color: 'var(--theme-danger)',
  },
  {
    key: 'GUEST_RECOGNITION',
    label: 'Smart Guest Recognition & Personalization',
    description: 'AI-powered guest intelligence that aggregates stay history, spending patterns, and preferences to generate actionable guest profiles for personalized service.',
    icon: '\u2605',
    color: '#7c3aed',
  },
  {
    key: 'HOUSEKEEPING_OPTIMIZER',
    label: 'AI Housekeeping Optimizer',
    description: 'AI generates optimized cleaning priority lists based on departures, arrivals, VIP status, staff availability, and floor proximity for faster room turnaround.',
    icon: '\u2728',
    color: '#0ea5e9',
  },
  {
    key: 'OCCUPANCY_FORECAST',
    label: 'Occupancy & Revenue Forecast',
    description: 'AI forecasts occupancy, ADR, RevPAR, and revenue for next 30/60/90 days with confidence bands and demand warnings.',
    icon: '\uD83D\uDCC8',
    color: '#f59e0b',
  },
  {
    key: 'UPSELL_ENGINE',
    label: 'Upsell & Cross-Sell Engine',
    description: 'AI identifies room upgrade, F&B, and late checkout upsell opportunities at check-in, during stay, and pre-departure.',
    icon: '\uD83D\uDCB0',
    color: '#10b981',
  },
  {
    key: 'STAFF_ASSISTANT',
    label: 'Natural Language Staff Assistant',
    description: 'Chat-based AI assistant for staff to query occupancy, revenue, guest info, and housekeeping status using natural language.',
    icon: '\uD83D\uDCAC',
    color: '#6366f1',
  },
  {
    key: 'FOLIO_ANOMALY',
    label: 'Folio Anomaly Detection',
    description: 'AI scans open folios for rate mismatches, duplicate postings, missing nights, and credit risks.',
    icon: '\uD83D\uDEE1',
    color: '#ef4444',
  },
  {
    key: 'CORPORATE_CREDIT_RISK',
    label: 'Corporate Credit Risk Scoring',
    description: 'AI scores corporate accounts based on payment behavior, credit utilization, and overdue invoices to flag risky accounts.',
    icon: '\u26A0',
    color: '#f97316',
  },
];

const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' };
const labelStyle = { display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: '0.3rem' };
const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, background: '#fff' };

function PmsAiFeatureConfig({ token, selectedProperty }) {
  /* ── AI Provider Config state ── */
  const [nlpForm, setNlpForm] = useState({
    nlp_provider: 'CLAUDE', nlp_api_key: '', nlp_model: 'claude-haiku-4-5-20251001',
    nlp_max_tokens: 500, nlp_temperature: 0.3, nlp_monthly_cost_cap: 50,
  });
  const [maskedKey, setMaskedKey] = useState('');
  const [keyEditing, setKeyEditing] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [usageCost, setUsageCost] = useState(0);

  /* ── Feature toggles state ── */
  const [features, setFeatures] = useState({}); // { REVENUE_MANAGER: { is_enabled, config } }

  /* ── UI state ── */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('provider');

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  /* ── Load NLP Config ── */
  const loadNlpConfig = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/pms/nlp-config', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setNlpForm({
        nlp_provider: data.nlp_provider || 'CLAUDE',
        nlp_api_key: '',
        nlp_model: data.nlp_model || 'claude-haiku-4-5-20251001',
        nlp_max_tokens: data.nlp_max_tokens || 500,
        nlp_temperature: data.nlp_temperature ?? 0.3,
        nlp_monthly_cost_cap: data.nlp_monthly_cost_cap || 50,
      });
      setMaskedKey(data.nlp_api_key_masked || '');
      setKeyEditing(false);
      setUsageCount(data.current_month_usage_count || 0);
      setUsageCost(data.current_month_estimated_cost || 0);
    } catch (err) {
      setError(err?.message || 'Failed to load NLP config');
    }
  }, [headers]);

  /* ── Load Feature Toggles (chain-level, no property required) ── */
  const loadFeatures = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/ai/features', { headers: headers() });
      const data = await res.json();
      if (res.ok) setFeatures(data.features || {});
    } catch { /* ignore */ }
  }, [headers]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadNlpConfig(), loadFeatures()]).finally(() => setLoading(false));
  }, [loadNlpConfig, loadFeatures]);

  /* ── Save NLP Config ── */
  const saveNlpConfig = async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      const body = {
        nlp_provider: nlpForm.nlp_provider,
        nlp_api_key: keyEditing ? nlpForm.nlp_api_key : `...${maskedKey.replace('...', '')}`,
        nlp_model: nlpForm.nlp_model,
        nlp_max_tokens: nlpForm.nlp_max_tokens,
        nlp_temperature: nlpForm.nlp_temperature,
        nlp_monthly_cost_cap: nlpForm.nlp_monthly_cost_cap,
      };
      const res = await fetch('/api/v1/pms/nlp-config', { method: 'POST', headers: headers(true), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setMessage('AI Provider configuration saved successfully');
      await loadNlpConfig();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  /* ── Test Connection ── */
  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const body = { nlp_api_key: keyEditing ? nlpForm.nlp_api_key : undefined };
      const res = await fetch('/api/v1/pms/nlp-config/test', { method: 'POST', headers: headers(true), body: JSON.stringify(body) });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally { setTesting(false); }
  };

  /* ── Toggle Feature ── */
  const toggleFeature = async (featureKey, enabled) => {
    try {
      const res = await fetch('/api/v1/ai/features', {
        method: 'POST', headers: headers(true),
        body: JSON.stringify({ feature_key: featureKey, is_enabled: enabled }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setFeatures(prev => ({ ...prev, [featureKey]: { ...prev[featureKey], is_enabled: enabled } }));
      setMessage(`${featureKey.replace(/_/g, ' ')} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const providerModels = MODELS[nlpForm.nlp_provider] || [];
  const costCapPct = nlpForm.nlp_monthly_cost_cap > 0 ? Math.min(100, (usageCost / nlpForm.nlp_monthly_cost_cap) * 100) : 0;

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>Loading AI settings...</div>;

  return (
    <div style={{ padding: '1rem', maxWidth: 800 }}>
      <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>AI Features & Configuration</h2>
      <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
        Configure AI provider, manage API keys, and enable/disable AI-powered features globally.
      </p>

      {error && <div style={{ background: '#fef2f2', color: 'var(--theme-danger)', padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '0.8rem', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #fecaca' }}>{error} <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--theme-danger)', cursor: 'pointer', fontWeight: 700 }}>x</button></div>}
      {message && <div style={{ background: '#f0fdf4', color: 'var(--theme-success)', padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '0.8rem', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #bbf7d0' }}>{message} <button onClick={() => setMessage('')} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--theme-success)', cursor: 'pointer', fontWeight: 700 }}>x</button></div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '1rem', borderBottom: '2px solid var(--theme-border)' }}>
        {[{ key: 'provider', label: 'AI Provider' }, { key: 'features', label: 'Feature Toggles' }].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ padding: '0.6rem 1.2rem', border: 'none', borderBottom: activeTab === tab.key ? '2px solid var(--theme-primary)' : '2px solid transparent',
              background: 'none', fontWeight: activeTab === tab.key ? 700 : 500, color: activeTab === tab.key ? 'var(--theme-primary)' : 'var(--theme-text-secondary)',
              cursor: 'pointer', fontSize: '0.9rem', marginBottom: -2 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════ TAB 1: AI Provider ═══════ */}
      {activeTab === 'provider' && (
        <>
          {/* Provider Selection */}
          <div style={sectionStyle}>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.8rem' }}>AI Provider</div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              {PROVIDERS.map(p => (
                <label key={p.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', padding: '0.5rem 1rem',
                  background: nlpForm.nlp_provider === p.value ? '#dbeafe' : '#fff', border: `1px solid ${nlpForm.nlp_provider === p.value ? '#3b82f6' : 'var(--theme-border)'}`,
                  borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', color: nlpForm.nlp_provider === p.value ? '#1e40af' : 'var(--theme-text-secondary)' }}>
                  <input type="radio" name="provider" value={p.value} checked={nlpForm.nlp_provider === p.value}
                    onChange={() => {
                      const models = MODELS[p.value] || [];
                      setNlpForm(f => ({ ...f, nlp_provider: p.value, nlp_model: models[0]?.value || '' }));
                    }} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>

          {/* API Key & Model */}
          <div style={sectionStyle}>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.8rem' }}>API Configuration</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>API Key</label>
                {!keyEditing ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ ...inputStyle, background: 'var(--theme-border-light)', display: 'inline-block', width: 'auto', flex: 1 }}>
                      {maskedKey || 'Not configured'}
                    </span>
                    <button onClick={() => setKeyEditing(true)}
                      style={{ padding: '0.5rem 0.8rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                      Change Key
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input type={showKey ? 'text' : 'password'} value={nlpForm.nlp_api_key}
                      onChange={e => setNlpForm(f => ({ ...f, nlp_api_key: e.target.value }))}
                      placeholder={nlpForm.nlp_provider === 'CLAUDE' ? 'sk-ant-...' : 'sk-...'}
                      style={{ ...inputStyle, flex: 1 }} />
                    <button onClick={() => setShowKey(!showKey)}
                      style={{ padding: '0.5rem 0.6rem', background: 'var(--theme-border)', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: '0.8rem' }}>
                      {showKey ? 'Hide' : 'Show'}
                    </button>
                    <button onClick={() => { setKeyEditing(false); setNlpForm(f => ({ ...f, nlp_api_key: '' })); }}
                      style={{ padding: '0.5rem 0.6rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, cursor: 'pointer', fontSize: '0.8rem', color: 'var(--theme-danger)' }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Model</label>
                <select value={nlpForm.nlp_model} onChange={e => setNlpForm(f => ({ ...f, nlp_model: e.target.value }))} style={selectStyle}>
                  {providerModels.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Monthly Cost Cap (USD)</label>
                <input type="number" min={1} max={10000} value={nlpForm.nlp_monthly_cost_cap}
                  onChange={e => setNlpForm(f => ({ ...f, nlp_monthly_cost_cap: Number(e.target.value) }))}
                  style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Usage Stats */}
          <div style={sectionStyle}>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.8rem' }}>Current Month Usage</div>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div><span style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>Requests:</span> <strong>{usageCount}</strong></div>
              <div><span style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>Cost:</span> <strong>${usageCost.toFixed(4)}</strong></div>
              <div><span style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>Cap:</span> <strong>${nlpForm.nlp_monthly_cost_cap}</strong></div>
            </div>
            <div style={{ marginTop: '0.5rem', height: 8, background: 'var(--theme-border)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${costCapPct}%`, height: '100%', background: costCapPct > 80 ? 'var(--theme-danger)' : costCapPct > 50 ? '#ca8a04' : 'var(--theme-success)', borderRadius: 4, transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)', marginTop: '0.3rem' }}>{costCapPct.toFixed(1)}% of monthly cap used</div>
          </div>

          {/* Test + Save */}
          <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1.2rem', flexWrap: 'wrap' }}>
            <button onClick={testConnection} disabled={testing}
              style={{ padding: '0.5rem 1.2rem', background: '#fff', color: 'var(--theme-primary)', border: '2px solid var(--theme-primary)', borderRadius: 6, cursor: testing ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            <button onClick={saveNlpConfig} disabled={saving}
              style={{ padding: '0.5rem 1.2rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              {saving ? 'Saving...' : 'Save Provider Config'}
            </button>
          </div>

          {testResult && (
            <div style={{ marginTop: '0.8rem', padding: '0.7rem 1rem', borderRadius: 6, fontSize: '0.85rem', fontWeight: 600,
              background: testResult.success ? '#f0fdf4' : '#fef2f2', color: testResult.success ? 'var(--theme-success)' : 'var(--theme-danger)',
              border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}` }}>
              {testResult.success
                ? `Connection successful! Model: ${testResult.model}, Response: ${testResult.response_time_ms}ms`
                : `Connection failed: ${testResult.error}`}
            </div>
          )}
        </>
      )}

      {/* ═══════ TAB 2: Feature Toggles ═══════ */}
      {activeTab === 'features' && (
        <>
          {!maskedKey && (
            <div style={{ background: '#fef3c7', color: '#92400e', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #fde68a' }}>
              AI Provider not configured. Please go to "AI Provider" tab and set up your API key first.
            </div>
          )}

          {AI_FEATURES.map(feat => {
            const state = features[feat.key] || { is_enabled: false };
            return (
              <div key={feat.key} style={{ ...sectionStyle, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: 44, height: 44, borderRadius: 8, background: state.is_enabled ? feat.color : 'var(--theme-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                  {feat.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>{feat.label}</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)', marginTop: '0.2rem' }}>{feat.description}</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: 50, height: 26, flexShrink: 0 }}>
                  <input type="checkbox" checked={state.is_enabled} onChange={() => toggleFeature(feat.key, !state.is_enabled)}
                    disabled={!maskedKey}
                    style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{
                    position: 'absolute', cursor: maskedKey ? 'pointer' : 'not-allowed', top: 0, left: 0, right: 0, bottom: 0,
                    background: state.is_enabled ? 'var(--theme-success)' : '#cbd5e1', borderRadius: 26, transition: 'background 0.3s',
                  }}>
                    <span style={{
                      position: 'absolute', height: 20, width: 20, left: state.is_enabled ? 26 : 3, bottom: 3,
                      background: '#fff', borderRadius: '50%', transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </span>
                </label>
              </div>
            );
          })}

          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, fontSize: '0.82rem', color: '#0369a1' }}>
            <strong>How it works:</strong> When enabled, the Revenue Manager analyzes your hotel's historical occupancy, current booking pace, and inventory to recommend optimal room rates. Recommendations appear in the Revenue Manager dashboard under Front Office where you can Accept or Reject each suggestion.
          </div>
        </>
      )}
    </div>
  );
}

export default PmsAiFeatureConfig;
